import type z from "zod";
import { schemas } from "../api/generated";

type ProblemDetails = z.infer<typeof schemas.ProblemDetails>;

export const getUploadFileCustomErrorMessage = (error: ProblemDetails) => {
  if (error.type === "https://problems-registry.smartbear.com/bad-request/") {
    if (error.title === "No File Uploaded") {
      return "No file was uploaded.";
    }
    if (error.title === "Invalid File Extension") {
      return "The file uploaded has an invalid file extension.";
    }
    if (error.title === "File Size Too Large") {
      return "The file uploaded exceeded 30 KB in size.";
    }
    if (error.title === "Invalid File Format") {
      return "The file uploaded was not recognized as a valid 7-Zip file.";
    }
  }
  return "Unexpected error";
};

export const getAssignUserCustomErrorMessage = (error: ProblemDetails) => {
  if (
    error.type ===
    "https://problems-registry.smartbear.com/missing-body-property/"
  ) {
    if (error.title === "Missing Required Body Property") {
      return "User and File IDs are required";
    }
  }
  if (
    error.type ===
    "https://problems-registry.smartbear.com/invalid-body-property-format/"
  ) {
    if (error.title === "User ID is invalid") {
      return "User ID is invalid";
    }
  }
  if (error.type === "https://problems-registry.smartbear.com/not-found/") {
    if (error.title === "File not found") {
      return "File not found";
    } else if (error.title === "User not found") {
      return "User not found";
    }
  }
  return "Unknown error";
};

export const getDeleteUserCustomErrorMessage = (error: ProblemDetails) => {
  if (error.type === "https://problems-registry.smartbear.com/not-found/") {
    if (error.title === "User not found") {
      return "User not found. Please refresh the page and try again.";
    }
  }
  const unexpectedErrorMessage =
    "An unexpected error occurred while deleting the user. Please try again later.";
  if (error.type === "https://problems-registry.smartbear.com/server-error/") {
    if (error.title === "Error deleting user") {
      return unexpectedErrorMessage;
    }
  }
  return unexpectedErrorMessage;
};

export const getDeleteFileCustomErrorMessage = (error: ProblemDetails) => {
  if (error.type === "https://problems-registry.smartbear.com/not-found/") {
    return "File not found. Please refresh the page and try again.";
  }
  return "An unexpected error occurred while deleting the file. Please try again later.";
};

export const getRegisterCustomErrorMessage = (error: ProblemDetails) => {
  if (error) {
    if (
      error.type === "https://problems-registry.smartbear.com/already-exists/"
    ) {
      if (error.title === "User Exists") {
        return "User already exists";
      }
    }
    if (
      error.type === "https://problems-registry.smartbear.com/server-error/"
    ) {
      if (error.title === "Error registering new user") {
        return "Error registering new user";
      }
    }
    if (error.title === "Feature Disabled") {
      return "Registration of new administrator users has been temporarily disabled.";
    }
  }
  return "Something went wrong try again";
};

export function getLoginCustomErrorMessage(error: ProblemDetails) {
  if (error) {
    if (
      error.type ===
      "https://problems-registry.smartbear.com/missing-body-property/"
    ) {
      if (error.title === "Invalid Body Property Format") {
        if (error.errors?.some((e) => e.pointer === "#/email")) {
          return "Email cannot be empty!";
        } else if (error?.errors?.some((e) => e.pointer === "#/password")) {
          return "Password cannot be empty!";
        }
      }
    } else if (
      error.type ===
      "https://problems-registry.smartbear.com/invalid-body-property-format/"
    ) {
      if (error.title === "Invalid Body Property Format") {
        if (error.errors?.some((e) => e.pointer === "#/email")) {
          return "A valid email address or username is required.";
        } else if (error?.errors?.some((e) => e.pointer === "#/password")) {
          return "A valid password is required.";
        }
      }
    } else if (
      error.type === "https://problems-registry.smartbear.com/unauthorized/"
    ) {
      if (error.title === "Invalid Credentials") {
        return "Invalid Credentials";
      }
    }
  }

  return "An error occurred during login.";
}

export function getUnassignFileCustomErrorMessage(error: ProblemDetails) {
  if (error) {
    if (
      error.type ===
      "https://problems-registry.smartbear.com/invalid-body-property-format"
    ) {
      if (error.title === "Invalid Body Property Format") {
        if (error.errors?.some((e) => e.pointer === "#/fileName")) {
          return "Failed to rename file because the file name was invalid";
        }
      }
    } else if (
      error.type === "https://problems-registry.smartbear.com/not-found"
    ) {
      if (error.title === "Invalid Body Property Format") {
        if (error.errors?.some((e) => e.pointer === "#/email")) {
          return "A valid email address or username is required.";
        } else if (error?.errors?.some((e) => e.pointer === "#/password")) {
          return "A valid password is required.";
        }
      }
    } else if (
      error.type === "https://problems-registry.smartbear.com/unauthorized"
    ) {
      if (error.title === "Invalid Credentials") {
        return "Failed to authorize";
      }
    }
  }

  return "Failed to unassign file from user";
}

export function getUpdateFileCustomErrorMessage(error: ProblemDetails) {
  if (error) {
    if (
      error.type ===
      "https://problems-registry.smartbear.com/invalid-body-property-format"
    ) {
      if (error.errors?.some((e) => e.pointer === "#/fileName")) {
        return "Failed to rename file because the file name was invalid";
      }
    } else if (
      error.type === "https://problems-registry.smartbear.com/not-found"
    ) {
      return "Failed to update file because it could not be found";
    } else if (
      error.type === "https://problems-registry.smartbear.com/unauthorized"
    ) {
      if (error.title === "Invalid Credentials") {
        return "Failed to authorize file update";
      }
    }
  }

  return "Failed to update file";
}
